import { useState, useEffect } from 'react'
import { Plus, Send, Clock, Users, Eye, Edit2, Trash2, X, BarChart3, Download } from 'lucide-react'
import { adminAPI } from '../services/api'
import pushIcon from '../assets/sidebar-mob-logo.png'

// Компонент таймера обратного отсчета
const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime()
      const target = new Date(targetDate).getTime()
      const difference = target - now

      if (difference <= 0) {
        return 'Час відправки настав'
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24))
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((difference % (1000 * 60)) / 1000)

      if (days > 0) {
        return `${days}д ${hours}г ${minutes}х`
      } else if (hours > 0) {
        return `${hours}г ${minutes}х ${seconds}с`
      } else if (minutes > 0) {
        return `${minutes}х ${seconds}с`
      } else {
        return `${seconds}с`
      }
    }

    setTimeLeft(calculateTimeLeft())
    
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => clearInterval(timer)
  }, [targetDate])

  return <span className="font-mono">{timeLeft}</span>
}

const Notifications = () => {
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showStatsModal, setShowStatsModal] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [modalMode, setModalMode] = useState('create') // 'create', 'edit'
  const [detailedStats, setDetailedStats] = useState(null)
  const [loadingStats, setLoadingStats] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    scheduledAt: '',
    filters: {
      allUsers: false,
      cities: [],
      birthdayToday: false
    }
  })
  const [availableCities, setAvailableCities] = useState([])
  const [birthdayUsersCount, setBirthdayUsersCount] = useState(0)
  const [loadingBirthdayCount, setLoadingBirthdayCount] = useState(false)
  
  // Фильтры для истории
  const [statusFilter, setStatusFilter] = useState('all') // 'all', 'sent', 'pending', 'failed'
  const [dateFilter, setDateFilter] = useState('all') // 'all', 'today', 'week', 'month'

  useEffect(() => {
    fetchNotifications()
    fetchAvailableCities()
    fetchBirthdayUsersCount()
  }, [])

  // Блокируем скролл фона когда открыта модалка
  useEffect(() => {
    if (showModal || showDeleteModal || showStatsModal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    
    // Очищаем при размонтировании
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [showModal, showDeleteModal, showStatsModal])

  const fetchAvailableCities = async () => {
    try {
      const response = await adminAPI.getRegions()
      if (response.data.success) {
        setAvailableCities(response.data.regions.map(region => region.name))
      }
    } catch (error) {
      console.error('Error fetching cities:', error)
    }
  }

  const fetchBirthdayUsersCount = async () => {
    try {
      setLoadingBirthdayCount(true)
      const response = await adminAPI.getBirthdayUsers()
      
      if (response.data.success) {
        const count = response.data.count || 0
        setBirthdayUsersCount(count)
      } else {
        setBirthdayUsersCount(0)
      }
    } catch (error) {
      console.error('Error fetching birthday users count:', error)
      setBirthdayUsersCount(0)
    } finally {
      setLoadingBirthdayCount(false)
    }
  }

  const fetchNotifications = async () => {
    try {
      setLoading(true)
      const response = await adminAPI.getNotifications()
      
      if (response.data.success) {
        // Маппим поля API к ожидаемому формату
        const mappedNotifications = (response.data.notifications || []).map(notification => {
          // Парсим target_users если это JSON строка
          let parsedFilters = {}
          let parsedUserCount = notification.userCount || 0
          
          if (notification.target_users) {
            try {
              const parsed = JSON.parse(notification.target_users)
              parsedFilters = parsed.filters || {}
              parsedUserCount = parsed.userCount || notification.userCount || 0
              console.log('Parsed target_users:', parsed)
            } catch (e) {
              console.warn('Failed to parse target_users:', e)
              parsedFilters = {}
            }
          }
          
          return {
            id: notification.id,
            title: notification.title,
            message: notification.message,
            scheduledAt: notification.scheduledAt || notification.scheduled_at,
            sentAt: notification.sentAt || notification.sent_at,
            status: notification.status,
            userCount: parsedUserCount,
            filters: parsedFilters,
            delivery_stats: notification.delivery_stats, // Добавляем delivery_stats
            createdBy: notification.createdBy || notification.created_by,
            createdByName: notification.createdByName || notification.created_by_name,
            createdAt: notification.createdAt || notification.created_at,
            updatedAt: notification.updatedAt || notification.updated_at
          }
        })
        
        setNotifications(mappedNotifications)
      }
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateNotification = () => {
    setSelectedNotification(null)
    setModalMode('create')
    setFormData({
      title: '',
      message: '',
      scheduledAt: '',
      filters: {
        allUsers: false,
        cities: [],
        birthdayToday: false
      }
    })
    setShowModal(true)
  }

  const handleCreateBirthdayNotification = () => {
    setSelectedNotification(null)
    setModalMode('create')
    setFormData({
      title: '🎉 З Днем народження!',
      message: 'Вітаємо з Днем народження! Бажаємо здоров\'я, щастя та успіхів! Спеціальна знижка чекає на Вас у додатку!',
      scheduledAt: '',
      filters: {
        allUsers: false,
        cities: [],
        birthdayToday: true
      }
    })
    setShowModal(true)
  }

  const handleEditNotification = (notification) => {
    setSelectedNotification(notification)
    setModalMode('edit')
    setFormData({
      title: notification.title,
      message: notification.message,
      scheduledAt: notification.scheduledAt ? new Date(notification.scheduledAt).toISOString().slice(0, 16) : '',
      filters: {
        allUsers: notification.filters?.allUsers || false,
        cities: notification.filters?.cities || [],
        birthdayToday: notification.filters?.birthdayToday || false
      }
    })
    setShowModal(true)
  }

  const handleDeleteNotification = (notification) => {
    setSelectedNotification(notification)
    setShowDeleteModal(true)
  }

  const handleModalClose = () => {
    setShowModal(false)
    setSelectedNotification(null)
  }

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false)
    setSelectedNotification(null)
  }

  const handleShowStats = async (notification) => {
    setSelectedNotification(notification)
    setLoadingStats(true)
    setShowStatsModal(true)
    
    try {
      console.log('Notification data:', notification)
      
      // Если у уведомления уже есть delivery_stats, используем их
      if (notification.delivery_stats) {
        console.log('Using existing delivery_stats:', notification.delivery_stats)
        setDetailedStats(notification.delivery_stats)
        setLoadingStats(false)
        return
      }
      
      // Если delivery_stats нет, делаем запрос к API
      console.log('Fetching stats for notification ID:', notification.id)
      const response = await adminAPI.getNotificationStats(notification.id)
      console.log('Stats API response:', response.data)
      
      if (response.data.success) {
        // Используем delivery_stats из ответа
        const stats = response.data.delivery_stats || response.data.stats || response.data
        setDetailedStats(stats)
      } else {
        console.log('API returned success: false')
        setDetailedStats(null)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
      console.error('Error response:', error.response?.data)
      setDetailedStats(null)
    } finally {
      setLoadingStats(false)
    }
  }

  const handleStatsModalClose = () => {
    setShowStatsModal(false)
    setSelectedNotification(null)
    setDetailedStats(null)
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('filters.')) {
      const filterKey = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        filters: {
          ...prev.filters,
          [filterKey]: type === 'checkbox' ? checked : value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }))
    }
  }

  const handleCityToggle = (city) => {
    setFormData(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        cities: prev.filters.cities.includes(city)
          ? prev.filters.cities.filter(c => c !== city)
          : [...prev.filters.cities, city]
      }
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const submitData = {
        title: formData.title,
        message: formData.message,
        scheduledAt: formData.scheduledAt ? new Date(formData.scheduledAt).toISOString() : null,
        filters: formData.filters
      }

      if (modalMode === 'create') {
        await adminAPI.createNotification(submitData)
      } else {
        await adminAPI.updateNotification(selectedNotification.id, submitData)
      }

      fetchNotifications()
      handleModalClose()
    } catch (error) {
      console.error('Error saving notification:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteConfirm = async () => {
    try {
      await adminAPI.deleteNotification(selectedNotification.id)
      fetchNotifications()
      handleDeleteModalClose()
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  }

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 text-xs font-semibold rounded-full flex items-center space-x-1"
    switch (status) {
      case 'sent':
        return `${baseClasses} bg-green-100 text-green-800 border border-green-200`
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800 border border-yellow-200`
      case 'partially_sent':
        return `${baseClasses} bg-orange-100 text-orange-800 border border-orange-200`
      case 'failed':
        return `${baseClasses} bg-red-100 text-red-800 border border-red-200`
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 border border-gray-200`
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'sent': return 'Відправлено'
      case 'pending': return 'Очікує відправки'
      case 'partially_sent': return 'Відправлено'
      case 'failed': return 'Помилка відправки'
      default: return 'Невідомий статус'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return '✅'
      case 'pending':
        return '⏳'
      case 'failed':
        return '❌'
      default:
        return '❓'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Фильтрация уведомлений
  const filteredNotifications = notifications.filter(notification => {
    // Фильтр по статусу
    if (statusFilter !== 'all' && notification.status !== statusFilter) {
      return false
    }
    
    // Фильтр по дате
    if (dateFilter !== 'all') {
      const notifDate = new Date(notification.createdAt)
      const now = new Date()
      const daysDiff = Math.floor((now - notifDate) / (1000 * 60 * 60 * 24))
      
      if (dateFilter === 'today' && daysDiff > 0) return false
      if (dateFilter === 'week' && daysDiff > 7) return false
      if (dateFilter === 'month' && daysDiff > 30) return false
    }
    
    return true
  })

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Пуш-повідомлення</h1>
          <p className="text-gray-600">Управління повідомленнями користувачам</p>
          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
            <span>Всього: {notifications.length}</span>
            <span className="text-green-600">Відправлено: {notifications.filter(n => n.status === 'sent').length}</span>
            <span className="text-yellow-600">Очікує: {notifications.filter(n => n.status === 'pending').length}</span>
            <span className="text-red-600">Помилки: {notifications.filter(n => n.status === 'failed').length}</span>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleCreateNotification}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>Створити повідомлення</span>
          </button>
        </div>
      </div>



      {/* Список повідомлень */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Повідомлення</h3>
          <div className="flex items-center space-x-3">
            {/* Фильтр по дате */}
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="input text-sm py-1.5"
            >
              <option value="all">Всі дати</option>
              <option value="today">Сьогодні</option>
              <option value="week">За тиждень</option>
              <option value="month">За місяць</option>
            </select>

            {/* Фильтр по статусу */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input text-sm py-1.5"
            >
              <option value="all">Всі статуси</option>
              <option value="sent">Відправлено</option>
              <option value="pending">Очікує</option>
              <option value="failed">Помилки</option>
              <option value="partially_sent">Частково</option>
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredNotifications.map((notification) => (
              <div key={notification.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-medium text-gray-900">{notification.title}</h4>
                      <span className={getStatusBadge(notification.status)}>
                        <span>{getStatusIcon(notification.status)}</span>
                        <span>
                          {getStatusText(notification.status)}
                        </span>
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{notification.message}</p>
                    
                    {/* Информация о датах и таймер */}
                    <div className="space-y-2">
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>Створено: {formatDate(notification.createdAt)}</span>
                      {notification.scheduledAt && (
                        <span>Заплановано: {formatDate(notification.scheduledAt)}</span>
                      )}
                      {notification.sentAt && (
                        <span>Відправлено: {formatDate(notification.sentAt)}</span>
                      )}
                        {notification.updatedAt && notification.updatedAt !== notification.createdAt && (
                          <span>Оновлено: {formatDate(notification.updatedAt)}</span>
                        )}
                      </div>
                      
                      {/* Таймер до отправки */}
                      {notification.status === 'pending' && notification.scheduledAt && (
                        <div className="flex items-center space-x-2">
                          <Clock size={14} className="text-yellow-600" />
                          <span className="text-sm text-yellow-700 font-medium">
                            До відправки: <CountdownTimer targetDate={notification.scheduledAt} />
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>ID: {notification.id}</span>
                        <span>Користувачів: {notification.userCount || 0}</span>
                        <span>Ціль: {
                          notification.filters?.birthdayToday ? '🎉 Іменинники сьогодні' :
                          notification.filters?.allUsers ? 'Всі користувачі' : 
                          notification.filters?.cities?.length > 0 ? 
                            `Міста: ${notification.filters.cities.join(', ')}` : 
                            'Не вказано'
                        }</span>
                        <span>Автор: {notification.createdByName || 'Невідомо'}</span>
                      </div>
                      
                      {/* Отладочная информация */}
                      {/* {process.env.NODE_ENV === 'development' && (
                        <div className="mt-2 p-2 bg-gray-100 rounded text-xs">
                          <strong>Debug:</strong> filters = {JSON.stringify(notification.filters)}
                        </div>
                      )} */}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {(notification.status === 'sent' || notification.status === 'partially_sent') && notification.delivery_stats && (
                      <button
                        onClick={() => handleShowStats(notification)}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                        title="Детальна статистика"
                      >
                        <BarChart3 size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => handleEditNotification(notification)}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Редагувати"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteNotification(notification)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Видалити"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {filteredNotifications.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {notifications.length === 0 ? 'Повідомлення не знайдені' : 'Немає повідомлень за обраними фільтрами'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Модальне вікно створення/редагування */}
      {showModal && (
        <div className="modal-overlay">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900">
                {modalMode === 'create' ? 'Створити повідомлення' : 'Редагувати повідомлення'}
              </h2>
              <button
                onClick={handleModalClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Заголовок *
                </label>
                <input
                  type="text"
                  required
                  className="input"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Введіть заголовок повідомлення"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Текст повідомлення *
                </label>
                <textarea
                  required
                  rows={3}
                  className="input"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Введіть текст повідомлення"
                />
              </div>

              {/* Превью уведомления */}
              {(formData.title || formData.message) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📱 Превью на телефоні
                  </label>
                  <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl p-4 border-2 border-gray-300">
                    <div className="bg-white rounded-lg shadow-lg p-3 max-w-xs">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg overflow-hidden">
                          <img 
                            src={pushIcon} 
                            alt="Push icon" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-900 mb-1">
                            {formData.title || 'Заголовок'}
                          </p>
                          <p className="text-xs text-gray-600 line-clamp-2">
                            {formData.message || 'Текст повідомлення'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            зараз
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Дата та час відправки
                </label>
                <input
                  type="datetime-local"
                  className="input"
                  value={formData.scheduledAt}
                  onChange={(e) => setFormData({...formData, scheduledAt: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Залиште порожнім для негайної відправки
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Цільові користувачі
                </label>
                
                <div className="space-y-3">
                  <div className="flex items-center ">
                    <input
                      type="checkbox"
                      id="allUsers"
                      name="filters.allUsers"
                      checked={formData.filters.allUsers}
                      onChange={handleChange}
                      disabled={formData.filters.birthdayToday}
                      className="mr-2"
                    />
                    <label htmlFor="allUsers" className="text-sm text-gray-700">
                      Всім користувачам
                    </label>
                  </div>

                  <div className="flex ">
                    <input
                      type="checkbox"
                      id="birthdayToday"
                      name="filters.birthdayToday"
                      checked={formData.filters.birthdayToday}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <label htmlFor="birthdayToday" className="text-sm text-gray-700 flex items-center space-x-2">
  
                      <span className="font-medium">Іменинникам сьогодні</span>
                      {birthdayUsersCount > 0 && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-purple-200 text-purple-800 rounded-full">
                          {birthdayUsersCount}
                        </span>
                      )}
                    </label>
                  </div>
                  
                  {!formData.filters.allUsers && !formData.filters.birthdayToday && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Оберіть міста
                      </label>
                      <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded p-2">
                        {availableCities.map(city => (
                          <label key={city} className="flex items-center text-sm">
                            <input
                              type="checkbox"
                              checked={formData.filters.cities.includes(city)}
                              onChange={() => handleCityToggle(city)}
                              className="mr-2"
                            />
                            {city}
                          </label>
                        ))}
                      </div>
                      {formData.filters.cities.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Обрано: {formData.filters.cities.join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              </div>

              <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0 bg-gray-50">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="btn btn-secondary"
                >
                  Скасувати
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? 'Збереження...' : modalMode === 'create' ? 'Створити' : 'Зберегти'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Модальне вікно підтвердження видалення */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Видалити повідомлення?
              </h3>
              <p className="text-gray-600 mb-6">
                Ви впевнені, що хочете видалити повідомлення "{selectedNotification?.title}"? 
                Цю дію неможливо скасувати.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteModalClose}
                  className="btn btn-secondary"
                >
                  Скасувати
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="btn btn-danger"
                >
                  Видалити
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно детальной статистики */}
      {showStatsModal && (
        <div className="modal-overlay">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900">
                Детальна статистика
              </h2>
              <button
                onClick={handleStatsModalClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {loadingStats ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : detailedStats ? (
                <div className="space-y-6">
                  {/* Заголовок уведомления */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">{selectedNotification?.title}</h3>
                    <p className="text-sm text-gray-600">{selectedNotification?.message}</p>
                  </div>

                  {/* Общая статистика */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <Users size={20} className="text-blue-600" />
                        <span className="font-semibold text-blue-900">Цільова аудиторія</span>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">{detailedStats.total_target_users || 0}</div>
                      <div className="text-sm text-blue-700">користувачів</div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <Send size={20} className="text-green-600" />
                        <span className="font-semibold text-green-900">Успішно відправлено</span>
                      </div>
                      <div className="text-2xl font-bold text-green-600">{detailedStats.successfully_sent || 0}</div>
                      <div className="text-sm text-green-700">повідомлень</div>
                    </div>
                  </div>

                  {/* Прогресс-бар */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Прогрес доставки</span>
                      <span className="text-sm text-gray-500">
                        {detailedStats.delivery_rate || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-green-500 h-3 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${detailedStats.delivery_rate || 0}%`
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Детальная статистика */}
                  <div className="grid grid-cols-1 gap-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Розподіл по статусах</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="text-sm text-gray-700">Успішно доставлено</span>
                          </div>
                          <span className="font-semibold text-green-600">{detailedStats.successfully_sent || 0}</span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                            <span className="text-sm text-gray-700">Немає push-токена</span>
                          </div>
                          <span className="font-semibold text-gray-600">{detailedStats.users_without_tokens || 0}</span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <span className="text-sm text-gray-700">Помилка відправки</span>
                          </div>
                          <span className="font-semibold text-red-600">{detailedStats.failed_sends || 0}</span>
                        </div>
                      </div>
                    </div>

                    {/* Процент охвата */}
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <BarChart3 size={20} className="text-purple-600" />
                        <span className="font-semibold text-purple-900">Охоплення аудиторії</span>
                      </div>
                      <div className="text-2xl font-bold text-purple-600">
                        {detailedStats.total_target_users > 0 
                          ? Math.round((detailedStats.users_with_tokens / detailedStats.total_target_users) * 100)
                          : 0}%
                      </div>
                      <div className="text-sm text-purple-700">
                        {detailedStats.users_with_tokens || 0} з {detailedStats.total_target_users || 0} користувачів мають push-токени
                      </div>
                      <div className="text-xs text-purple-600 mt-1">
                        * Користувачі без токенів = додаток не встановлено
                      </div>
                    </div>
                  </div>

                  {/* Кнопка экспорта */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        const csvData = [
                          ['Метрика', 'Значення'],
                          ['Цільова аудиторія', detailedStats.total_target_users || 0],
                          ['Користувачі з токенами', detailedStats.users_with_tokens || 0],
                          ['Користувачі без токенів', detailedStats.users_without_tokens || 0],
                          ['Успішно відправлено', detailedStats.successfully_sent || 0],
                          ['Помилки відправки', detailedStats.failed_sends || 0],
                          ['Охоплення аудиторії (%)', detailedStats.total_target_users > 0 
                            ? Math.round((detailedStats.users_with_tokens / detailedStats.total_target_users) * 100)
                            : 0],
                          ['Прогрес доставки (%)', detailedStats.delivery_rate || 0]
                        ]
                        
                        const csvContent = csvData.map(row => row.join(',')).join('\n')
                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
                        const link = document.createElement('a')
                        const url = URL.createObjectURL(blob)
                        link.setAttribute('href', url)
                        link.setAttribute('download', `notification-stats-${selectedNotification?.id}.csv`)
                        link.style.visibility = 'hidden'
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                      }}
                      className="btn btn-secondary flex items-center space-x-2"
                    >
                      <Download size={16} />
                      <span>Експорт CSV</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">{selectedNotification?.title}</h3>
                    <p className="text-sm text-gray-600 mb-4">{selectedNotification?.message}</p>
                    <p className="text-gray-500">
                      Статистика доставки недоступна для цього повідомлення
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      Можливо, повідомлення було відправлено до впровадження системи статистики
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Notifications
